import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import IntakeFormFields from './IntakeFormFields';

const mono = { fontFamily: "'DM Mono', monospace" };
const sans = { fontFamily: "'DM Sans', sans-serif" };
const serif = { fontFamily: "'Cormorant Garamond', serif" };

const NAVY = '#0A1828';
const COPPER = '#7A5230';
const COPPER_HOVER = '#8C6240';
const TEAL = '#1A6B6B';
const SURFACE = '#FFFFFF';
const BG = '#FAFAF8';
const MUTED = 'rgba(10,24,40,0.46)';
const BORDER = 'rgba(10,24,40,0.10)';

const API =
  process.env.REACT_APP_WORKFLOW_BASE_URL ||
  process.env.REACT_APP_API_URL ||
  'https://mri-portal.onrender.com';

const RESPONSIVE_CSS = `
.cp-tab-nav-inner::-webkit-scrollbar { display: none; }
.cp-tab-nav-inner { scrollbar-width: none; -ms-overflow-style: none; }
@media (max-width: 640px) {
  .cp-header { padding: 24px 20px !important; }
  .cp-tab-btn { padding: 0 16px !important; }
  .cp-grid-4 { grid-template-columns: 1fr 1fr !important; }
  .cp-upload-zone { padding: 24px !important; }
  .cp-content { padding: 24px 20px 60px !important; }
  .cp-intake-actions { flex-direction: column !important; }
  .cp-intake-actions > button { flex: none !important; width: 100% !important; }
}
`;

async function notifyWorkflow(endpoint, body) {
  try {
    const token = process.env.REACT_APP_INTERNAL_API_KEY;
    const headers = { 'Content-Type': 'application/json' };
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const resp = await fetch(`${API}${endpoint}`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!resp.ok) {
      console.warn(`Workflow notification failed (${endpoint}): ${resp.status}`);
    }
  } catch (err) {
    console.warn(`Workflow notification error (${endpoint}):`, err);
  }
}

const TABS = ['Overview', 'Documents', 'Intake', 'Timeline', 'Report'];

const STAGE_ORDER = [
  { key: 'engagement_opened', label: 'Engagement Opened' },
  { key: 'intake_complete', label: 'Intake Complete' },
  { key: 'evidence_lock_sent', label: 'Evidence Lock Sent' },
  { key: 'evidence_lock_signed', label: 'Evidence Lock Signed' },
  { key: 'presentation_1_scheduled', label: 'Presentation 1 Scheduled' },
  { key: 'presentation_1_delivered', label: 'Presentation 1 Delivered' },
  { key: 'presentation_2_delivered', label: 'Presentation 2 Delivered' },
  { key: 'report_ready', label: 'Report Ready' },
  { key: 'report_presented', label: 'Report Presented' },
];

function mapCurrentStage(eng) {
  if (eng.presented_at) return 'report_presented';
  if (eng.final_report_html) return 'report_ready';
  if (eng.presentation_2_delivered) return 'presentation_2_delivered';
  if (eng.presentation_1_delivered) return 'presentation_1_delivered';
  if (eng.presentation_1_scheduled_at) return 'presentation_1_scheduled';
  if (eng.evidence_lock_signed_at) return 'evidence_lock_signed';
  if (eng.evidence_lock_sent_at) return 'evidence_lock_sent';
  if (eng.intake_form_completed_at) return 'intake_complete';
  return 'engagement_opened';
}

function stageLabel(stageKey) {
  const s = STAGE_ORDER.find((st) => st.key === stageKey);
  return s ? s.label : stageKey;
}

function nextStepHeadline(stageKey) {
  const idx = STAGE_ORDER.findIndex((s) => s.key === stageKey);
  if (idx < 0 || idx >= STAGE_ORDER.length - 1) return 'Engagement Complete';
  const next = STAGE_ORDER[idx + 1];
  switch (next.key) {
    case 'intake_complete': return 'Complete Your Intake';
    case 'evidence_lock_sent': return 'Evidence Lock In Preparation';
    case 'evidence_lock_signed': return 'Review and Sign Evidence Lock';
    case 'presentation_1_scheduled': return 'First Presentation Being Scheduled';
    case 'presentation_1_delivered': return 'First Presentation In Preparation';
    case 'presentation_2_delivered': return 'Second Presentation In Preparation';
    case 'report_ready': return 'Final Report In Assembly';
    case 'report_presented': return 'Report Ready for Presentation';
    default: return 'Your Next Step';
  }
}

function nextStepText(stageKey) {
  const idx = STAGE_ORDER.findIndex((s) => s.key === stageKey);
  if (idx < 0 || idx >= STAGE_ORDER.length - 1) return 'Your engagement is complete.';
  const next = STAGE_ORDER[idx + 1];
  switch (next.key) {
    case 'intake_complete': return 'Please complete the intake form and upload your supporting documents.';
    case 'evidence_lock_sent': return 'Your advisor is preparing your evidence lock document for review.';
    case 'evidence_lock_signed': return 'Please review and sign the evidence lock document sent to you.';
    case 'presentation_1_scheduled': return 'Your advisor will schedule your first presentation shortly.';
    case 'presentation_1_delivered': return 'Your first presentation is being prepared.';
    case 'presentation_2_delivered': return 'Your second presentation is being prepared.';
    case 'report_ready': return 'Your final report is being assembled by your advisor.';
    case 'report_presented': return 'Your report is ready and will be presented to you soon.';
    default: return 'Your advisor will contact you with next steps.';
  }
}

function fmtDate(ts) {
  if (!ts) return null;
  try {
    return new Date(ts).toLocaleDateString('en-US', {
      month: 'long', day: 'numeric', year: 'numeric',
    });
  } catch {
    return null;
  }
}

// ── Section header ────────────────────────────────────────────────────────

function SectionHeader({ children }) {
  return (
    <div style={{
      ...mono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase',
      color: COPPER, marginBottom: 20,
      display: 'flex', alignItems: 'center', gap: 12,
    }}>
      <span style={{ width: 24, height: 1, background: COPPER, flexShrink: 0 }} />
      {children}
    </div>
  );
}

// ── Tab: Overview ─────────────────────────────────────────────────────────

function OverviewTab({ eng }) {
  const stage = eng.current_stage || mapCurrentStage(eng);
  const headline = nextStepHeadline(stage);
  const description = nextStepText(stage);
  const advisorName = eng.vp_name || 'Your Advisor';
  const engRef = ((eng.upload_token || eng.id || '').toString().slice(0, 10)).toUpperCase();
  const openedDate = fmtDate(eng.created_at) || '--';

  return (
    <div>
      {/* NEXT STEP PANEL */}
      <div style={{
        background: NAVY,
        padding: '28px 32px',
        marginBottom: 24,
      }}>
        <div style={{
          ...mono, fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: COPPER, marginBottom: 10,
        }}>
          NEXT STEP
        </div>
        <div style={{
          ...serif, fontSize: 22, fontWeight: 300, color: '#FFFFFF', lineHeight: 1.3,
        }}>
          {headline}
        </div>
        <div style={{
          ...sans, fontSize: 14, color: 'rgba(255,255,255,0.70)',
          marginTop: 8, lineHeight: 1.55,
        }}>
          {description}
        </div>
      </div>

      {/* ADVISOR CARD */}
      <div style={{
        background: BG,
        border: `1px solid ${BORDER}`,
        padding: '20px 24px',
        marginBottom: 24,
      }}>
        <div style={{
          ...mono, fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase',
          color: COPPER, marginBottom: 8,
        }}>
          YOUR ADVISOR
        </div>
        <div style={{
          ...serif, fontSize: 20, fontWeight: 400, color: NAVY, lineHeight: 1.2,
        }}>
          {advisorName}
        </div>
        <div style={{
          ...mono, fontSize: 9, color: MUTED, marginTop: 4, letterSpacing: '0.08em',
        }}>
          Dedicated engagement lead
        </div>
      </div>

      {/* ENGAGEMENT SUMMARY GRID */}
      <div className="cp-grid-4" style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        display: 'grid',
        gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
      }}>
        <GridCell label="Reference" value={engRef} asMono last={false} />
        <GridCell label="Stage" value={stageLabel(stage)} last={false} />
        <GridCell label="Opened" value={openedDate} last={false} />
        <GridCell label="Engagement" value="Market Readiness" last />
      </div>
    </div>
  );
}

function GridCell({ label, value, asMono, last }) {
  return (
    <div style={{
      padding: '20px 24px',
      borderRight: last ? 'none' : '1px solid rgba(10,24,40,0.08)',
      minWidth: 0,
    }}>
      <div style={{
        ...mono, fontSize: 8, letterSpacing: '0.22em', textTransform: 'uppercase',
        color: MUTED, marginBottom: 10,
      }}>
        {label}
      </div>
      <div
        style={
          asMono
            ? { ...mono, fontSize: 14, color: NAVY, letterSpacing: '0.04em', wordBreak: 'break-word' }
            : { ...serif, fontSize: 22, fontWeight: 400, color: NAVY, lineHeight: 1.2, wordBreak: 'break-word' }
        }
      >
        {value}
      </div>
    </div>
  );
}

// ── Tab: Documents ────────────────────────────────────────────────────────

function DocumentsTab({ eng, onRefresh }) {
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');

  const docs = (() => {
    try {
      const is = eng.intake_structured;
      const parsed = typeof is === 'string' ? JSON.parse(is) : is;
      if (parsed && parsed._evidence_binder && parsed._evidence_binder.documents) {
        return parsed._evidence_binder.documents;
      }
    } catch { /* ignore */ }
    return [];
  })();

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      const token = eng.upload_token;
      if (!token) {
        setUploadMsg('Upload not available. Contact your advisor.');
        setUploading(false);
        return;
      }
      const resp = await fetch(`${API}/upload/${token}`, {
        method: 'POST',
        body: formData,
      });
      if (resp.ok) {
        setUploadMsg('File uploaded successfully.');
        if (onRefresh) onRefresh();
      } else {
        setUploadMsg('Upload failed. Please try again.');
      }
    } catch {
      setUploadMsg('Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const badgeBackground = (type) => {
    const t = (type || '').toUpperCase();
    if (t.includes('TAX')) return TEAL;
    if (t.includes('P&L') || t === 'PL' || t.includes('PROFIT')) return COPPER;
    if (t.includes('BALANCE')) return NAVY;
    return NAVY;
  };

  return (
    <div>
      <div style={{ marginBottom: 40 }}>
        <SectionHeader>UPLOADED DOCUMENTS</SectionHeader>

        {docs.length === 0 ? (
          <div style={{
            ...sans, fontSize: 14, color: MUTED,
            padding: '16px 0', borderTop: `1px solid ${BORDER}`,
          }}>
            No documents uploaded yet.
          </div>
        ) : (
          <div style={{ borderTop: `1px solid ${BORDER}` }}>
            {docs.map((doc, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '14px 0',
                borderBottom: '1px solid rgba(10,24,40,0.06)',
              }}>
                <div style={{
                  ...mono, fontSize: 8, letterSpacing: '0.12em', textTransform: 'uppercase',
                  background: badgeBackground(doc.type), color: '#FFFFFF',
                  padding: '3px 10px', flexShrink: 0,
                }}>
                  {doc.type || 'DOC'}
                </div>
                <div style={{ ...sans, fontSize: 14, color: NAVY, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {doc.name || doc.filename || `Document ${i + 1}`}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div>
        <SectionHeader>UPLOAD DOCUMENTS</SectionHeader>

        <label className="cp-upload-zone" style={{
          display: 'block',
          border: '2px dashed rgba(122,82,48,0.30)',
          background: 'rgba(122,82,48,0.03)',
          padding: 40,
          textAlign: 'center',
          cursor: uploading ? 'not-allowed' : 'pointer',
        }}>
          <div style={{ ...sans, fontSize: 14, color: MUTED, marginBottom: 10 }}>
            {uploading ? 'Uploading your file.' : 'Click to select a file, or drop it here.'}
          </div>
          <div style={{
            ...mono, fontSize: 9, letterSpacing: '0.18em',
            textTransform: 'uppercase', color: COPPER,
          }}>
            PDF · XLSX · DOCX · CSV
          </div>
          <input
            type="file"
            onChange={handleUpload}
            disabled={uploading}
            style={{ display: 'none' }}
          />
        </label>

        {uploadMsg && (
          <div style={{
            ...sans, fontSize: 13, marginTop: 12,
            color: uploadMsg.includes('success') ? TEAL : 'rgba(160,40,40,0.85)',
          }}>
            {uploadMsg}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Tab: Intake ───────────────────────────────────────────────────────────

function IntakeTab({ eng, onRefresh }) {
  const initiallySubmitted = !!eng.intake_form_completed_at;
  const [localSubmitted, setLocalSubmitted] = useState(initiallySubmitted);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    setLocalSubmitted(!!eng.intake_form_completed_at);
  }, [eng.intake_form_completed_at]);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const handleSaveProgress = async (values) => {
    const { error } = await supabase
      .from('cordis_engagements')
      .update({ intake_form_answers: values })
      .eq('id', eng.id);
    if (error) {
      showToast('error', 'Save failed. Please try again.');
      return;
    }
    showToast('success', 'Progress saved.');
    if (onRefresh) onRefresh();
  };

  const handleFinalSubmit = async (values) => {
    const now = new Date().toISOString();
    const { error } = await supabase
      .from('cordis_engagements')
      .update({
        intake_form_answers: values,
        intake_form_completed_at: now,
      })
      .eq('id', eng.id);
    if (error) {
      showToast('error', 'Submit failed. Please try again.');
      return;
    }
    notifyWorkflow('/notify/intake-submitted', {
      engagement_id: eng.id,
      client_name: eng.client_name || '',
      company: eng.company || '',
    });
    setLocalSubmitted(true);
    if (onRefresh) onRefresh();
  };

  const handleRequestChanges = async () => {
    setLocalSubmitted(false);
    notifyWorkflow('/notify/intake-updated', {
      engagement_id: eng.id,
      client_name: eng.client_name || '',
      company: eng.company || '',
    });
    showToast('info', 'Form unlocked. Your advisor has been notified.');
  };

  return (
    <div>
      <SectionHeader>INTAKE FORM</SectionHeader>

      {localSubmitted && (
        <div style={{
          background: 'rgba(26,107,107,0.08)',
          border: '1px solid rgba(26,107,107,0.28)',
          padding: '16px 20px',
          marginBottom: 20,
        }}>
          <div style={{
            ...mono, fontSize: 9, letterSpacing: '0.22em', textTransform: 'uppercase',
            color: TEAL, marginBottom: 6,
          }}>
            SUBMITTED
          </div>
          <div style={{ ...sans, fontSize: 14, color: NAVY, lineHeight: 1.5 }}>
            Your answers have been received. Your advisor has been notified.
          </div>
        </div>
      )}

      {toast && (
        <div style={{
          ...sans, fontSize: 13,
          color: toast.kind === 'error' ? 'rgba(160,40,40,0.9)' : TEAL,
          background: toast.kind === 'error' ? 'rgba(160,40,40,0.08)' : 'rgba(26,107,107,0.08)',
          border: `1px solid ${toast.kind === 'error' ? 'rgba(160,40,40,0.28)' : 'rgba(26,107,107,0.20)'}`,
          padding: '12px 16px', marginBottom: 20,
        }}>
          {toast.msg}
        </div>
      )}

      <div style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        padding: 32,
      }}>
        <IntakeFormFields
          key={localSubmitted ? 'view' : 'edit'}
          initialValues={eng.intake_form_answers || {}}
          readOnly={localSubmitted}
          onSaveProgress={handleSaveProgress}
          onSubmit={handleFinalSubmit}
          onRequestChanges={handleRequestChanges}
        />
      </div>
    </div>
  );
}

// ── Tab: Timeline ─────────────────────────────────────────────────────────

function TimelineTab({ eng }) {
  const current = eng.current_stage || mapCurrentStage(eng);
  const currentIdx = STAGE_ORDER.findIndex((s) => s.key === current);

  const STAGE_TIMESTAMPS = {
    engagement_opened: eng.created_at,
    intake_complete: eng.intake_form_completed_at,
    evidence_lock_sent: eng.evidence_lock_sent_at,
    evidence_lock_signed: eng.evidence_lock_signed_at,
    presentation_1_scheduled: eng.calendly_2_sent_at,
    presentation_1_delivered: eng.presentation_1_delivered ? eng.updated_at : null,
    presentation_2_delivered: eng.presentation_2_delivered ? eng.updated_at : null,
    report_ready: eng.data_locked_at,
    report_presented: eng.presented_at,
  };

  return (
    <div>
      <SectionHeader>ENGAGEMENT TIMELINE</SectionHeader>

      <div style={{ position: 'relative', paddingLeft: 40 }}>
        {/* vertical line */}
        <div style={{
          position: 'absolute',
          left: 15,
          top: 8,
          bottom: 8,
          width: 2,
          background: BORDER,
        }} />

        {STAGE_ORDER.map((stage, i) => {
          const done = i < currentIdx;
          const isCurrent = i === currentIdx;
          const reached = done || isCurrent;
          const ts = reached ? fmtDate(STAGE_TIMESTAMPS[stage.key]) : null;

          let circleStyle;
          if (isCurrent) {
            circleStyle = {
              background: COPPER,
              border: 'none',
              boxShadow: '0 0 0 4px rgba(122,82,48,0.22)',
            };
          } else if (done) {
            circleStyle = {
              background: TEAL,
              border: 'none',
            };
          } else {
            circleStyle = {
              background: 'transparent',
              border: '2px solid rgba(10,24,40,0.15)',
            };
          }

          return (
            <div key={stage.key} style={{
              position: 'relative',
              paddingBottom: i < STAGE_ORDER.length - 1 ? 32 : 0,
              minHeight: 32,
            }}>
              <div style={{
                position: 'absolute',
                left: -33,
                top: 0,
                width: 16,
                height: 16,
                borderRadius: '50%',
                boxSizing: 'border-box',
                ...circleStyle,
              }} />

              <div style={{
                ...sans,
                fontSize: 14,
                fontWeight: isCurrent ? 600 : 400,
                color: reached ? NAVY : MUTED,
                lineHeight: 1.2,
              }}>
                {stage.label}
              </div>

              {ts && (
                <div style={{
                  ...mono, fontSize: 9, color: MUTED,
                  marginTop: 4, letterSpacing: '0.08em',
                }}>
                  {ts}
                </div>
              )}

              {isCurrent && !ts && (
                <div style={{
                  ...mono, fontSize: 9, color: COPPER,
                  marginTop: 4, letterSpacing: '0.18em', textTransform: 'uppercase',
                }}>
                  Current
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Tab: Report ───────────────────────────────────────────────────────────

function ReportTab({ eng }) {
  if (!eng.final_report_html) {
    const message = eng.presented_at
      ? 'Your report is being finalized. Please check back shortly.'
      : 'Your Market Readiness Intelligence report will be available here after your presentation session with your advisor.';

    return (
      <div style={{
        background: SURFACE,
        border: `1px solid ${BORDER}`,
        padding: '80px 40px',
        textAlign: 'center',
      }}>
        <div style={{
          ...mono, fontSize: 9, letterSpacing: '0.28em',
          textTransform: 'uppercase', color: COPPER, marginBottom: 16,
        }}>
          CORDIS GROUP
        </div>
        <div style={{
          ...serif, fontSize: 32, fontWeight: 300, color: NAVY,
          marginBottom: 20, lineHeight: 1.1,
        }}>
          Your Report
        </div>
        <div style={{
          width: 48, height: 1, background: COPPER,
          margin: '0 auto 24px',
        }} />
        <div style={{
          ...sans, fontSize: 14, color: MUTED,
          lineHeight: 1.65, maxWidth: 440, margin: '0 auto',
        }}>
          {message}
        </div>
      </div>
    );
  }

  return (
    <iframe
      srcDoc={eng.final_report_html}
      title="Market Readiness Intelligence Report"
      style={{
        width: '100%',
        height: '100vh',
        border: 'none',
        background: BG,
        display: 'block',
      }}
    />
  );
}

// ── Main ──────────────────────────────────────────────────────────────────

export default function ClientPortal() {
  const [eng, setEng] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('Overview');
  const [signOutHover, setSignOutHover] = useState(false);
  const navigate = useNavigate();

  const engId = localStorage.getItem('cordis_client_engagement_id');

  const fetchEngagement = useCallback(async () => {
    if (!engId) return;
    const { data, error } = await supabase
      .from('cordis_engagements')
      .select('*')
      .eq('id', engId)
      .single();

    if (error || !data) {
      localStorage.removeItem('cordis_client_engagement_id');
      navigate('/client-login');
      return;
    }
    setEng(data);
    setLoading(false);
  }, [engId, navigate]);

  useEffect(() => {
    if (!engId) {
      navigate('/client-login');
      return;
    }
    fetchEngagement();
  }, [engId, navigate, fetchEngagement]);

  if (loading || !eng) {
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        minHeight: '100vh', background: BG,
      }}>
        <div style={{
          ...mono, fontSize: 10, color: MUTED,
          letterSpacing: '0.22em', textTransform: 'uppercase',
        }}>
          LOADING
        </div>
      </div>
    );
  }

  const engRef = ((eng.upload_token || eng.id || '').toString().slice(0, 10)).toUpperCase();
  const clientDisplay = eng.client_name || 'Client';
  const companyDisplay = eng.company || '';

  const renderTab = () => {
    switch (activeTab) {
      case 'Overview': return <OverviewTab eng={eng} />;
      case 'Documents': return <DocumentsTab eng={eng} onRefresh={fetchEngagement} />;
      case 'Intake': return <IntakeTab eng={eng} onRefresh={fetchEngagement} />;
      case 'Timeline': return <TimelineTab eng={eng} />;
      case 'Report': return <ReportTab eng={eng} />;
      default: return null;
    }
  };

  const signOut = () => {
    localStorage.removeItem('cordis_client_engagement_id');
    navigate('/client-login');
  };

  return (
    <div style={{ width: '100%', minHeight: '100vh', background: BG }}>
      <style>{RESPONSIVE_CSS}</style>
      {/* HEADER */}
      <header className="cp-header" style={{
        background: NAVY,
        width: '100%',
        boxSizing: 'border-box',
        padding: '32px clamp(20px, 4vw, 48px)',
        borderBottom: '1px solid rgba(122,82,48,0.35)',
      }}>
        {/* Top row */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
          flexWrap: 'wrap',
          gap: 16,
        }}>
          <div style={{
            ...mono, fontSize: 9, letterSpacing: '0.28em',
            color: COPPER, textTransform: 'uppercase',
          }}>
            CORDIS GROUP
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 20 }}>
            <div style={{
              ...mono, fontSize: 9, letterSpacing: '0.18em',
              color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
            }}>
              REF · {engRef}
            </div>
            <button
              onClick={signOut}
              onMouseEnter={() => setSignOutHover(true)}
              onMouseLeave={() => setSignOutHover(false)}
              style={{
                ...mono, fontSize: 9, letterSpacing: '0.18em', textTransform: 'uppercase',
                background: 'transparent',
                border: `1px solid ${signOutHover ? 'rgba(255,255,255,0.45)' : 'rgba(255,255,255,0.20)'}`,
                color: signOutHover ? '#FFFFFF' : 'rgba(255,255,255,0.75)',
                padding: '0 16px',
                height: 36,
                cursor: 'pointer',
                transition: 'all 0.15s ease',
              }}
            >
              Sign Out
            </button>
          </div>
        </div>

        {/* Client name */}
        <div className="cp-client-name" style={{
          ...serif, fontSize: 'clamp(28px, 8vw, 48px)', fontWeight: 300,
          color: '#FFFFFF', lineHeight: 1,
          wordBreak: 'break-word',
        }}>
          {clientDisplay}
        </div>

        {/* Company */}
        {companyDisplay && (
          <div style={{
            ...mono, fontSize: 10, letterSpacing: '0.18em',
            color: COPPER, textTransform: 'uppercase', marginTop: 10,
          }}>
            {companyDisplay}
          </div>
        )}

        {/* Engagement line */}
        <div style={{
          ...mono, fontSize: 9, letterSpacing: '0.14em',
          color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase',
          marginTop: companyDisplay ? 18 : 14,
        }}>
          Market Readiness Intelligence · Confidential
        </div>
      </header>

      {/* TAB NAV */}
      <div style={{
        background: SURFACE,
        borderBottom: `1px solid ${BORDER}`,
        width: '100%',
        boxSizing: 'border-box',
      }}>
        <div className="cp-tab-nav-inner" style={{
          display: 'flex',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          padding: '0 clamp(20px, 4vw, 48px)',
          whiteSpace: 'nowrap',
        }}>
          {TABS.map((tab) => {
            const active = activeTab === tab;
            return (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className="cp-tab-btn"
                style={{
                  ...mono,
                  fontSize: 9,
                  letterSpacing: '0.18em',
                  textTransform: 'uppercase',
                  background: 'none',
                  border: 'none',
                  borderRadius: 0,
                  borderBottom: `2px solid ${active ? COPPER : 'transparent'}`,
                  color: active ? COPPER : MUTED,
                  padding: '0 22px',
                  height: 48,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  flexShrink: 0,
                  transition: 'color 0.15s ease, border-color 0.15s ease',
                }}
              >
                {tab}
              </button>
            );
          })}
        </div>
      </div>

      {/* CONTENT */}
      <div className="cp-content" style={{
        width: '100%',
        boxSizing: 'border-box',
        padding: '32px clamp(20px, 4vw, 32px) 80px',
        maxWidth: 960,
        margin: '0 auto',
      }}>
        {renderTab()}
      </div>
    </div>
  );
}
